/**
 * AdminHealthPage — „Systemstatus".
 *
 * Aggregierte Live-Health-Checks für den Operator:
 *   - Virenscanner (ClamAV) — bekannte Fallback-Logik aus Iter18.3
 *   - Datenbank (Supabase/PostgreSQL) — Live-Ping + Latenz
 *   - KI-Service (Claude via Emergent LLM Key) — Konfig + letzter Erfolg/Fehler
 *   - E-Mail-Versand (Resend) — Konfig + letzter Sendestatus
 *   - System-Ressourcen — Uptime + Speicher + Load
 *
 * Auto-Refresh alle 20 Sek + Manual-Refresh. Kein Polling während Page nicht
 * sichtbar (Browser drosselt Intervalle in Background-Tabs eh automatisch).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api, extractError } from "../lib/api";
import {
  Heartbeat,
  ShieldCheck,
  Shield,
  WarningCircle,
  Database,
  Sparkle,
  EnvelopeSimple,
  HardDrives,
  ArrowsClockwise,
  CheckCircle,
  Warning,
  XCircle,
} from "@phosphor-icons/react";


function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60_000) return "Gerade eben";
    if (diff < 3_600_000) return `Vor ${Math.floor(diff / 60_000)} Min.`;
    if (diff < 86_400_000) return `Vor ${Math.floor(diff / 3_600_000)} Std.`;
    return d.toLocaleString("de-AT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtUptime(seconds) {
  if (!seconds || seconds < 60) return `${seconds || 0} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ${Math.floor((seconds % 3600) / 60)} min`;
  const days = Math.floor(seconds / 86400);
  return `${days} Tag${days === 1 ? "" : "e"} ${Math.floor((seconds % 86400) / 3600)} h`;
}


// Status → visual mapping.
const STATUS_META = {
  ok:    { bar: "bg-emerald-500", icon: CheckCircle,    iconCls: "text-emerald-700", iconBg: "bg-emerald-50 border-emerald-200", label: "Gesund" },
  warn:  { bar: "bg-amber-500",   icon: Warning,        iconCls: "text-amber-700",   iconBg: "bg-amber-50 border-amber-200",       label: "Aufmerksamkeit" },
  error: { bar: "bg-red-500",     icon: XCircle,        iconCls: "text-red-700",     iconBg: "bg-red-50 border-red-200",           label: "Fehler" },
};

// id → which icon represents this domain (large left badge)
const CHECK_ICONS = {
  clamav:   ShieldCheck,
  database: Database,
  llm:      Sparkle,
  mailer:   EnvelopeSimple,
  system:   HardDrives,
};


function detailLines(check) {
  const id = check.id;
  const d = check.details || {};
  const out = [];

  if (id === "clamav") {
    if (d.available) {
      out.push(["Modus", "ClamAV-Signaturscan"]);
      if (d.info) out.push(["ClamAV-Version", d.info]);
      out.push(["Letzter erfolgreicher Scan", fmtRelative(d.last_success_at)]);
    } else {
      out.push(["Modus", "In-Process (Magic-Byte + Makro-/Skript-Block)"]);
      out.push(["Persistenz", "Keine — Dateien nur im RAM"]);
    }
    // "Letzte Datei-Prüfung" — neutral umbenannt, weil im In-Process-Modus
    // jede Prüfung eine echte Prüfung ist und kein "Fallback".
    if (d.last_fallback_at) {
      out.push(["Letzte Datei-Prüfung", fmtRelative(d.last_fallback_at)]);
    }
    out.push([
      "Geprüfte Dateien (24h)",
      String(d.fallback_scans_24h ?? 0),
      null,
    ]);
    if (d.in_skip_window) {
      out.push([
        "Fast-Skip-Fenster",
        `Aktiv · ${d.skip_window_remaining_seconds}s`,
      ]);
    }
  } else if (id === "database") {
    if (check.latency_ms !== null && check.latency_ms !== undefined) {
      out.push(["Antwort-Latenz", `${check.latency_ms} ms`]);
    }
  } else if (id === "llm") {
    if (d.model) out.push(["Modell", d.model]);
    out.push(["Letzte erfolgreiche Antwort", fmtRelative(d.last_success_at)]);
    if (d.last_error_at) {
      out.push(["Letzter Fehler", fmtRelative(d.last_error_at)]);
    }
    out.push([
      "Anfragen seit Start",
      `${d.success_count ?? 0} OK · ${d.failure_count ?? 0} Fehler`,
    ]);
  } else if (id === "mailer") {
    out.push(["Konfiguration", d.configured ? "Aktiv" : "Fehlt"]);
    if (d.disabled) out.push(["Hinweis", "Test-Modus aktiv (MAILER_DISABLE=true)"]);
    if (d.last_send_at) {
      out.push([
        "Letzter Versand",
        `${fmtRelative(d.last_send_at)} (${d.last_send_ok ? "erfolgreich" : "fehlgeschlagen"})`,
        d.last_send_ok === false ? "warn" : null,
      ]);
    }
  } else if (id === "system") {
    if (d.uptime_seconds !== undefined) {
      out.push(["Backend-Uptime", fmtUptime(d.uptime_seconds)]);
    }
    if (d.memory_rss_mb !== null && d.memory_rss_mb !== undefined) {
      out.push(["Speicherverbrauch (RSS)", `${d.memory_rss_mb.toFixed(0)} MB`]);
    }
    if (Array.isArray(d.load_avg) && d.load_avg.length === 3) {
      out.push([
        "CPU-Load (1/5/15 min)",
        d.load_avg.map((v) => v.toFixed(2)).join(" / "),
      ]);
    }
    if (d.started_at) out.push(["Gestartet am", fmtRelative(d.started_at)]);
  }
  return out;
}


function HealthCheckCard({ check }) {
  const meta = STATUS_META[check.status] || STATUS_META.warn;
  const DomainIcon = CHECK_ICONS[check.id] || Shield;
  const StatusIcon = meta.icon;
  const lines = detailLines(check);

  return (
    <div
      className="relative border border-ink-200 bg-white rounded-sm"
      data-testid={`health-card-${check.id}`}
    >
      <div className={`absolute top-0 left-0 h-1 w-24 ${meta.bar}`} aria-hidden="true" />
      <div className="px-6 py-5 flex items-start gap-4 flex-wrap">
        <div className={`h-11 w-11 shrink-0 flex items-center justify-center rounded-sm border ${meta.iconBg}`}>
          <DomainIcon size={20} weight="bold" className={meta.iconCls} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 inline-flex items-center gap-1.5">
            <StatusIcon size={11} weight="bold" className={meta.iconCls} />
            <span data-testid={`health-status-${check.id}`}>{meta.label}</span>
          </div>
          <div className="font-cabinet text-base text-ink-900 font-medium leading-tight">
            {check.label}
          </div>
          {check.note && (
            <p className="mt-1.5 text-sm text-warm-700 leading-relaxed">
              {check.note}
            </p>
          )}
          {lines.length > 0 && (
            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
              {lines.map(([label, value, tone], idx) => (
                <div key={idx}>
                  <dt className="text-warm-500 uppercase tracking-widest font-semibold">
                    {label}
                  </dt>
                  <dd
                    className={`font-medium ${
                      tone === "warn" ? "text-amber-700" : "text-ink-900"
                    }`}
                  >
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>
  );
}


function OverallBanner({ status, generatedAt, onRefresh, refreshing }) {
  const meta = STATUS_META[status] || STATUS_META.warn;
  const Icon = meta.icon;
  const headline = {
    ok: "Alle Systeme laufen normal",
    warn: "Eingeschränkter Betrieb",
    error: "Kritischer Zustand erkannt",
  }[status] || "Status wird geprüft";
  return (
    <div
      className={`relative border rounded-sm overflow-hidden ${
        status === "ok" ? "border-emerald-200 bg-emerald-50/60"
        : status === "warn" ? "border-amber-200 bg-amber-50/60"
        : "border-red-200 bg-red-50/60"
      }`}
      data-testid="health-overall-banner"
    >
      <div className={`absolute top-0 left-0 h-1 w-full ${meta.bar}`} aria-hidden="true" />
      <div className="px-6 py-5 flex items-center gap-4 flex-wrap">
        <Icon size={28} weight="bold" className={meta.iconCls} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-600">
            Gesamtstatus
          </div>
          <div className="font-cabinet text-xl text-ink-900 font-medium leading-tight">
            {headline}
          </div>
          {generatedAt && (
            <div className="text-[11px] text-warm-500 mt-1">
              Stand: {fmtRelative(generatedAt)} · automatische Aktualisierung alle 20 s
            </div>
          )}
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-sm border border-ink-300 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 transition-crisp hover:bg-ink-100 disabled:opacity-60"
          data-testid="health-refresh"
        >
          <ArrowsClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
          Aktualisieren
        </button>
      </div>
    </div>
  );
}


export default function AdminHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const { data: res } = await api.get("/admin/health");
      setData(res);
    } catch (err) {
      setError(extractError(err, "Systemstatus konnte nicht geladen werden."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 20_000);
    return () => clearInterval(t);
  }, [load]);

  const checks = useMemo(() => data?.checks || [], [data]);

  return (
    <AdminLayout title="Systemstatus" subtitle="Live-Health-Checks aller Subsysteme">
      <div className="max-w-5xl space-y-6" data-testid="admin-health-page">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 inline-flex items-center gap-1.5">
              <Heartbeat size={11} weight="bold" />
              Systemstatus
            </div>
            <h1 className="font-cabinet text-4xl tracking-tight text-ink-900 font-medium leading-none">
              Systemstatus
            </h1>
            <p className="text-sm text-warm-700 mt-2 max-w-2xl">
              Echtzeit-Übersicht über alle Subsysteme: Datei-Sicherheitsprüfung,
              Datenbank, KI-Service, E-Mail-Versand und Backend-Ressourcen.
              Aktualisiert automatisch alle 20 Sekunden.
            </p>
          </div>
        </div>

        {error && !data && (
          <div
            className="border border-status-abgelehnt-border bg-status-abgelehnt-bg rounded-sm p-4 text-sm text-status-abgelehnt-text"
            data-testid="health-error"
          >
            {error}
          </div>
        )}

        {loading && !data && (
          <div className="border border-ink-200 bg-white rounded-sm px-8 py-12 text-center">
            <div className="inline-flex items-center gap-2 text-sm text-warm-600">
              <span className="brand-dot" />
              <span className="brand-dot" />
              <span className="brand-dot" />
              <span className="ml-2">Systemstatus wird ermittelt …</span>
            </div>
          </div>
        )}

        {data && (
          <>
            <OverallBanner
              status={data.overall_status}
              generatedAt={data.generated_at}
              onRefresh={() => load(true)}
              refreshing={refreshing}
            />
            <div className="space-y-3">
              {checks.map((c) => (
                <HealthCheckCard key={c.id} check={c} />
              ))}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
